/* =========================================================================
   useTelemetryViewport — Reusable Headless Viewport Hook
   Pure viewport state management, trackpad pinch-zoom, mousewheel pan-scroll,
   boundary clamping, and live/paused streaming controls.
   ========================================================================= */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  MAX_HISTORY_MS,
  spanLabel,
  agoLabel,
  getActiveTelemetrySeriesStore,
} from '../utils/telemetry-helpers.utils';
import { isTimeRangeInMemory, getMissingTimeRange, getInMemoryTimeRange } from '../utils/telemetry-time-range.utils';
import { TelemetryViewportState } from '../types/telemetry-dashboard.types';

export interface UseTelemetryViewportOptions {
  /** Initial timeframe in minutes (default: 60) */
  timeframe?: number;
  /** Optional custom start time in epoch seconds */
  customStartTimeSec?: number | null;
  /** Optional callback to reset/update custom start time (e.g. when user manually pans/scrubs) */
  onCustomStartTimeChange?: (newStartSec: number | null) => void;
  /** Optional controlled viewport state from context or parent */
  viewport?: TelemetryViewportState;
  /** Optional controlled viewport setter */
  onViewportChange?: (vp: TelemetryViewportState) => void;
  /** Optional function to get simulation/data start timestamp in ms for start boundary clamping */
  getSimStartTimeMs?: () => number | null;
  /** Optional function to get current reference timestamp in ms (defaults to in-memory max data ts or Date.now()) */
  getReferenceTs?: (maxFoundTs: number) => number;
  /** Optional pluggable callback triggered when user pans into historical data */
  onFetchHistory?: (startSec: number, endSec: number) => Promise<boolean | void> | boolean | void;
  /** Debounce interval in ms for historical data fetching (default: 400) */
  fetchDebounceMs?: number;
  /** Flag to disable historical fetch (e.g. simulation completed or offline) */
  isHistoryFetchDisabled?: boolean;
}

export interface UseTelemetryViewportReturn {
  viewport: TelemetryViewportState;
  setViewport: (patch: Partial<TelemetryViewportState>) => void;
  resetViewport: (timeframeMinutes: number) => void;
  jumpToLive: () => void;
  fetchRangeIfNeeded: (startSec: number, endSec: number) => Promise<void>;
  onChartWheel: (e: WheelEvent) => void;
  getChartWrapRef: (id: string) => (el: HTMLElement | null) => void;
  cleanupWheelListeners: () => void;
  viewportLabel: string;
  isPaused: boolean;
  isAtStartBoundary: boolean;
  isLoadingHistory: boolean;
  setIsLoadingHistory: React.Dispatch<React.SetStateAction<boolean>>;
}

// Shared in-flight promise deduplicator across multiple hook instances
const inFlightRangeFetches = new Map<string, Promise<void>>();
let activeFetchPromise: Promise<any> | null = null;
let activeFetchKey: string = '';
let lastGlobalFetchKey: string = '';

// Shared subscriber registry to sync loading state across multiple hook instances
const historyLoadingListeners = new Set<(loading: boolean) => void>();
let globalHistoryLoadingCount = 0;

function notifyHistoryLoading(isLoading: boolean) {
  if (isLoading) {
    globalHistoryLoadingCount++;
  } else {
    globalHistoryLoadingCount = Math.max(0, globalHistoryLoadingCount - 1);
  }
  const isCurrentlyLoading = globalHistoryLoadingCount > 0;
  historyLoadingListeners.forEach((listener) => {
    try {
      listener(isCurrentlyLoading);
    } catch (e) {
      console.warn('Error in history loading listener:', e);
    }
  });
}

export function useTelemetryViewport(options: UseTelemetryViewportOptions = {}): UseTelemetryViewportReturn {
  const {
    timeframe = 60,
    customStartTimeSec = null,
    onCustomStartTimeChange,
    viewport: controlledViewport,
    onViewportChange: controlledSetViewport,
    getSimStartTimeMs,
    getReferenceTs: customGetReferenceTs,
    onFetchHistory,
    fetchDebounceMs = 400,
    isHistoryFetchDisabled = false,
  } = options;

  const [internalViewport, setInternalViewport] = useState<TelemetryViewportState>({
    spanMs: timeframe * 60000,
    offsetMs: 0,
  });

  const viewport = controlledViewport ?? internalViewport;
  const setViewportState = controlledSetViewport ?? setInternalViewport;

  const [isAtStartBoundary, setIsAtStartBoundary] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(() => globalHistoryLoadingCount > 0);

  useEffect(() => {
    const listener = (loading: boolean) => {
      setIsLoadingHistory(loading);
    };
    historyLoadingListeners.add(listener);
    return () => {
      historyLoadingListeners.delete(listener);
    };
  }, []);

  const isFetchingRef = useRef(false);
  const lastFetchedRangeRef = useRef<{ startSec: number; endSec: number } | null>(null);

  const vpRef = useRef(viewport);
  vpRef.current = viewport;

  const customStartTimeSecRef = useRef(customStartTimeSec);
  customStartTimeSecRef.current = customStartTimeSec;

  const onCustomStartTimeChangeRef = useRef(onCustomStartTimeChange);
  onCustomStartTimeChangeRef.current = onCustomStartTimeChange;

  const setViewportStateRef = useRef(setViewportState);
  setViewportStateRef.current = setViewportState;

  const getSimStartTimeMsRef = useRef(getSimStartTimeMs);
  getSimStartTimeMsRef.current = getSimStartTimeMs;

  const fetchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const rAfRef = useRef<number | null>(null);

  const onChartWheelRef = useRef<(e: WheelEvent) => void>(() => { });

  // Default reference timestamp computation
  const getReferenceTs = useCallback((maxFoundTs: number): number => {
    if (customGetReferenceTs) {
      return customGetReferenceTs(maxFoundTs);
    }
    if (maxFoundTs > 0) return maxFoundTs;
    if (getSimStartTimeMs) {
      const simStart = getSimStartTimeMs();
      if (simStart) return simStart;
    }
    return Date.now();
  }, [customGetReferenceTs, getSimStartTimeMs]);

  const getReferenceTsRef = useRef(getReferenceTs);
  getReferenceTsRef.current = getReferenceTs;

  // Pure helper to determine if viewport offset & span is at or before simulation start boundary
  const checkIsAtStartBoundary = useCallback(
    (offsetMs: number, spanMs: number): boolean => {
      const simStartMs = getSimStartTimeMs?.() ?? null;
      if (!simStartMs) return false;

      let maxFoundTs = 0;
      const seriesStore = getActiveTelemetrySeriesStore();
      if (seriesStore && seriesStore.length > 0) {
        for (const s of seriesStore) {
          if (s && s.data && s.data.length > 0) {
            const last = s.data[s.data.length - 1]?.value?.[0];
            if (typeof last === 'number' && last > maxFoundTs) {
              maxFoundTs = last;
            }
          }
        }
      }
      const refTs = getReferenceTs(maxFoundTs);
      if (!refTs) return false;

      if (refTs <= simStartMs) return true;

      const maxAllowedOffset = Math.max(0, refTs - (simStartMs + spanMs));
      return offsetMs >= maxAllowedOffset;
    },
    [getSimStartTimeMs, getReferenceTs]
  );

  const setViewport = useCallback((patch: Partial<TelemetryViewportState>) => {
    const nextVp = { ...vpRef.current, ...patch };
    vpRef.current = nextVp;
    setViewportState(nextVp);
    setIsAtStartBoundary(checkIsAtStartBoundary(nextVp.offsetMs, nextVp.spanMs));
  }, [setViewportState, checkIsAtStartBoundary]);

  const fetchRangeIfNeeded = useCallback(
    async (targetStartSec: number, targetEndSec: number) => {
      if (!onFetchHistory || targetStartSec >= targetEndSec) return;
      if (isTimeRangeInMemory(targetStartSec, targetEndSec)) return;

      const missing = getMissingTimeRange(targetStartSec, targetEndSec);
      if (!missing || missing.startSec >= missing.endSec) return;

      const simStartMs = getSimStartTimeMs?.() ?? null;
      const simStartSec = simStartMs ? Math.floor(simStartMs / 1000) : undefined;
      const boundedStartSec = simStartSec != null ? Math.max(simStartSec, missing.startSec) : missing.startSec;

      if (boundedStartSec >= missing.endSec) return;

      const key = `${boundedStartSec}-${missing.endSec}`;
      if (inFlightRangeFetches.has(key)) {
        notifyHistoryLoading(true);
        try {
          await inFlightRangeFetches.get(key);
        } finally {
          notifyHistoryLoading(false);
        }
        return;
      }

      const fetchPromise = (async () => {
        notifyHistoryLoading(true);
        try {
          await onFetchHistory(boundedStartSec, missing.endSec);
        } finally {
          notifyHistoryLoading(false);
          inFlightRangeFetches.delete(key);
        }
      })();

      inFlightRangeFetches.set(key, fetchPromise);
      await fetchPromise;
    },
    [onFetchHistory, getSimStartTimeMs]
  );

  const resetViewport = useCallback((tf: number) => {
    const spanMs = tf * 60000;
    const nextVp = { spanMs, offsetMs: 0, customStartMs: null };
    vpRef.current = nextVp;
    lastFetchedRangeRef.current = null;
    lastGlobalFetchKey = '';
    setViewportState(nextVp);
    setIsAtStartBoundary(checkIsAtStartBoundary(0, spanMs));
  }, [setViewportState, checkIsAtStartBoundary]);

  const jumpToLive = useCallback(() => {
    const spanMs = timeframe * 60000;
    const nextVp = { spanMs, offsetMs: 0, customStartMs: null };
    vpRef.current = nextVp;
    lastFetchedRangeRef.current = null;
    lastGlobalFetchKey = '';
    setViewportState(nextVp);
    setIsAtStartBoundary(checkIsAtStartBoundary(0, spanMs));
  }, [timeframe, setViewportState, checkIsAtStartBoundary]);

  // Synchronize start boundary state when viewport span or offset changes
  useEffect(() => {
    setIsAtStartBoundary(checkIsAtStartBoundary(viewport.offsetMs, viewport.spanMs));
  }, [checkIsAtStartBoundary, viewport.offsetMs, viewport.spanMs]);

  // Handle custom start time or timeframe changes
  const prevCustomStartRef = useRef<number | null>(customStartTimeSec);
  const prevTimeframeRef = useRef(timeframe);

  useEffect(() => {
    const isStartChanged = prevCustomStartRef.current !== customStartTimeSec;
    const isTfChanged = prevTimeframeRef.current !== timeframe;

    if (isStartChanged || isTfChanged) {
      prevCustomStartRef.current = customStartTimeSec;
      prevTimeframeRef.current = timeframe;

      if (customStartTimeSec != null) {
        // Locked window mode
        const targetStartSec = customStartTimeSec;
        const targetEndSec = customStartTimeSec + timeframe * 60;
        fetchRangeIfNeeded(targetStartSec, targetEndSec);

        const seriesStore = getActiveTelemetrySeriesStore();
        let maxFoundTs = 0;
        if (seriesStore && seriesStore.length > 0) {
          for (const s of seriesStore) {
            if (s && s.data && s.data.length > 0) {
              const last = s.data[s.data.length - 1]?.value?.[0];
              if (typeof last === 'number' && last > maxFoundTs) maxFoundTs = last;
            }
          }
        }
        const referenceTs = getReferenceTs(maxFoundTs);
        const spanMs = timeframe * 60000;
        const startMs = customStartTimeSec * 1000;
        const targetEndMs = startMs + spanMs;
        const targetOffsetMs = referenceTs > targetEndMs ? referenceTs - targetEndMs : 0;

        setViewport({ spanMs, offsetMs: targetOffsetMs, customStartMs: startMs });
      } else if (isTfChanged || isStartChanged) {
        // Live mode timeframe expansion
        const seriesStore = getActiveTelemetrySeriesStore();
        let maxFoundTs = 0;
        if (seriesStore && seriesStore.length > 0) {
          for (const s of seriesStore) {
            if (s && s.data && s.data.length > 0) {
              const last = s.data[s.data.length - 1]?.value?.[0];
              if (typeof last === 'number' && last > maxFoundTs) maxFoundTs = last;
            }
          }
        }
        const referenceTs = getReferenceTs(maxFoundTs);
        const currentSimSec = Math.floor(referenceTs / 1000);
        const targetStartSec = currentSimSec - timeframe * 60;

        fetchRangeIfNeeded(targetStartSec, currentSimSec);
        setViewport({ spanMs: timeframe * 60000, offsetMs: 0, customStartMs: null });
      }
    }
  }, [customStartTimeSec, timeframe, fetchRangeIfNeeded, getReferenceTs, setViewport, resetViewport]);

  // Reset lastFetchedRange when satellite or fetch handler changes
  useEffect(() => {
    lastFetchedRangeRef.current = null;
    lastGlobalFetchKey = '';
  }, [onFetchHistory]);

  // Dynamic historical fetching on offset change (debounced)
  useEffect(() => {
    if (isHistoryFetchDisabled || !onFetchHistory) return;
    if (viewport.offsetMs <= 0) return;

    if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);

    fetchDebounceRef.current = setTimeout(async () => {
      if (isFetchingRef.current) return;

      try {
        const memRange = getInMemoryTimeRange();
        let maxFoundTs = memRange ? memRange.maxSec * 1000 : 0;
        let minLoadedTs = memRange ? memRange.minSec * 1000 : Infinity;

        if (!memRange) {
          const seriesStore = getActiveTelemetrySeriesStore();
          if (seriesStore && seriesStore.length > 0) {
            for (const s of seriesStore) {
              if (s && s.data && s.data.length > 0) {
                const first = s.data[0]?.value?.[0];
                const last = s.data[s.data.length - 1]?.value?.[0];
                if (typeof first === 'number' && first > 0) {
                  minLoadedTs = Math.min(minLoadedTs, first);
                }
                if (typeof last === 'number' && last > maxFoundTs) {
                  maxFoundTs = last;
                }
              }
            }
          }
        }
        const referenceTs = getReferenceTs(maxFoundTs);
        const endMs = referenceTs - viewport.offsetMs;
        const startMs = endMs - viewport.spanMs;
        const simStartMs = getSimStartTimeMs?.() ?? null;

        // If simulation start has already been reached in memory, stop further calls
        if (simStartMs && minLoadedTs < Infinity && minLoadedTs <= simStartMs + 2000) {
          setIsAtStartBoundary(true);
          return;
        }

        if (simStartMs && endMs <= simStartMs) {
          setIsAtStartBoundary(true);
          return;
        }

        // If the entire requested window is already in memory, skip network fetch
        if (minLoadedTs < Infinity && startMs >= minLoadedTs) {
          return;
        }

        const boundedStartMs = simStartMs ? Math.max(startMs, simStartMs) : startMs;
        const boundedEndMs = Math.min(endMs, referenceTs);

        const startSec = Math.floor(boundedStartMs / 1000);
        const endSec = Math.floor(boundedEndMs / 1000);

        if (startSec >= endSec) return;

        const fetchKey = `${startSec}:${endSec}`;

        // If this exact range is already in-flight from another component instance, wait for it
        if (activeFetchKey === fetchKey && activeFetchPromise) {
          notifyHistoryLoading(true);
          try {
            await activeFetchPromise;
          } finally {
            notifyHistoryLoading(false);
          }
          return;
        }

        // If range already fetched globally, skip duplicate REST API calls
        if (lastGlobalFetchKey === fetchKey) return;

        if (
          lastFetchedRangeRef.current &&
          Math.abs(lastFetchedRangeRef.current.startSec - startSec) < 30 &&
          Math.abs(lastFetchedRangeRef.current.endSec - endSec) < 30
        ) {
          return;
        }

        lastFetchedRangeRef.current = { startSec, endSec };
        lastGlobalFetchKey = fetchKey;
        activeFetchKey = fetchKey;
        isFetchingRef.current = true;
        notifyHistoryLoading(true);

        const fetchPromise = Promise.resolve(onFetchHistory(startSec, endSec));
        activeFetchPromise = fetchPromise;

        const success = await fetchPromise;
        if (success === false) {
          setIsAtStartBoundary(true);
        }
      } catch (err) {
        console.warn('Failed to fetch historical telemetry chunk:', err);
        lastGlobalFetchKey = '';
        lastFetchedRangeRef.current = null;
      } finally {
        isFetchingRef.current = false;
        notifyHistoryLoading(false);
        activeFetchPromise = null;
        activeFetchKey = '';
      }
    }, fetchDebounceMs);

    return () => {
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
    };
  }, [viewport.offsetMs, viewport.spanMs, isHistoryFetchDisabled, onFetchHistory, fetchDebounceMs, getSimStartTimeMs, getReferenceTs]);

  // Wheel listener handler for pan / scrub
  const onChartWheel = useCallback((e: WheelEvent) => {
    const isShift = e.shiftKey;
    const absX = Math.abs(e.deltaX);
    const absY = Math.abs(e.deltaY);

    // 1. Precise gesture classification:
    // Trackpads inherently emit micro-horizontal deltaX even during intentional vertical scrolls.
    // Classify as horizontal gesture ONLY if:
    // - User holds Shift key (standard horizontal scroll modifier), OR
    // - Horizontal movement is strictly dominant over vertical movement (absX > absY) AND above micro-noise threshold (absX >= 2)
    const isHorizontalGesture = isShift || (absX > absY && absX >= 2);

    // If this is a vertical scroll (user scrolling the dashboard up/down):
    // NEVER call e.preventDefault(), allowing native, hardware-accelerated 60/120fps vertical page scrolling.
    if (!isHorizontalGesture) {
      return;
    }

    // 2. If a valid custom start time is active, completely freeze horizontal scrubbing:
    const isCustomStartLocked =
      (customStartTimeSecRef.current != null && customStartTimeSecRef.current > 0) ||
      (vpRef.current.customStartMs != null && vpRef.current.customStartMs > 0);

    if (isCustomStartLocked) {
      // Prevent browser default on Mac (e.g. swipe navigation back/forward in browser history)
      // but do NOT change offset, do NOT reset start time, and do NOT change viewport.
      e.preventDefault();
      return;
    }

    // 3. Otherwise (live mode or historical pan mode), perform horizontal pan/scrub:
    e.preventDefault();

    const vp = vpRef.current;
    const sMs = vp.spanMs;
    let oMs = vp.offsetMs;

    const clamp = (v: number, lim: number) => Math.max(-lim, Math.min(lim, v));
    const rawDelta = isShift ? e.deltaY : e.deltaX;
    const panDelta = clamp(rawDelta || 0, 100);

    if (panDelta) {
      oMs += panDelta * (sMs / 1500);

      // If user had a custom start time and is freely panning, clear it
      if (customStartTimeSecRef.current != null) {
        onCustomStartTimeChangeRef.current?.(null);
      }
    }

    // Boundary check against start time using in-memory reference timestamp
    const seriesStore = getActiveTelemetrySeriesStore();
    let maxFoundTs = 0;
    if (seriesStore && seriesStore.length > 0) {
      for (const s of seriesStore) {
        if (s && s.data && s.data.length > 0) {
          const last = s.data[s.data.length - 1]?.value?.[0];
          if (typeof last === 'number' && last > maxFoundTs) {
            maxFoundTs = last;
          }
        }
      }
    }
    const referenceTs = getReferenceTsRef.current(maxFoundTs);
    const simStartMs = getSimStartTimeMsRef.current?.() ?? null;

    if (simStartMs && referenceTs > simStartMs) {
      // Clamp so that window start (referenceTs - oMs - sMs) does not scroll before simStartMs
      const maxAllowedOffset = Math.max(0, referenceTs - (simStartMs + sMs));
      if (oMs >= maxAllowedOffset) {
        oMs = maxAllowedOffset;
        setIsAtStartBoundary(true);
      } else {
        setIsAtStartBoundary(false);
      }
    } else {
      oMs = Math.max(0, Math.min(oMs, MAX_HISTORY_MS - sMs));
      setIsAtStartBoundary(false);
    }

    // Clamp offsetMs to non-negative
    if (oMs < 0) {
      oMs = 0;
    }

    const nextVp = { spanMs: sMs, offsetMs: oMs, customStartMs: null };
    vpRef.current = nextVp;

    if (rAfRef.current) cancelAnimationFrame(rAfRef.current);
    rAfRef.current = requestAnimationFrame(() => {
      setViewportStateRef.current(nextVp);
    });
  }, []);

  onChartWheelRef.current = onChartWheel;

  // Wheel ref factory
  const wheelRefs = useRef<Record<string, (el: HTMLElement | null) => void>>({});
  const wheelNodes = useRef<Record<string, { el: HTMLElement; fn: (e: WheelEvent) => void }>>({});

  const getChartWrapRef = useCallback(
    (id: string) => {
      if (!wheelRefs.current[id]) {
        wheelRefs.current[id] = (el: HTMLElement | null) => {
          const old = wheelNodes.current[id];
          if (old && old.el && old.el !== el) {
            old.el.removeEventListener('wheel', old.fn);
          }
          if (!el) {
            delete wheelNodes.current[id];
            return;
          }
          if (old && old.el === el) return;
          const fn = (ev: WheelEvent) => onChartWheelRef.current(ev);
          el.addEventListener('wheel', fn, { passive: false });
          wheelNodes.current[id] = { el, fn };
        };
      }
      return wheelRefs.current[id];
    },
    []
  );

  const cleanupWheelListeners = useCallback(() => {
    Object.values(wheelNodes.current).forEach((o) => {
      if (o.el) o.el.removeEventListener('wheel', o.fn);
    });
    wheelNodes.current = {};
    wheelRefs.current = {};
  }, []);

  const viewportLabel = spanLabel(viewport.spanMs) +
    (viewport.offsetMs > 0 ? ' · ' + agoLabel(viewport.offsetMs) + ' ago' : ' · live');

  const isPaused = viewport.offsetMs > 0;

  useEffect(() => {
    return () => {
      if (rAfRef.current) cancelAnimationFrame(rAfRef.current);
      if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current);
      Object.values(wheelNodes.current).forEach((o) => {
        if (o.el) o.el.removeEventListener('wheel', o.fn);
      });
      wheelNodes.current = {};
      wheelRefs.current = {};
    };
  }, []);

  return {
    viewport,
    setViewport,
    resetViewport,
    jumpToLive,
    fetchRangeIfNeeded,
    onChartWheel,
    getChartWrapRef,
    cleanupWheelListeners,
    viewportLabel,
    isPaused,
    isLoadingHistory,
    setIsLoadingHistory,
    isAtStartBoundary,
  };
}

export default useTelemetryViewport;
